import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { Accordion, AccordionSummary, Box, Grid, IconButton, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { CostGroupDto, CreateCostGroupRequest } from '../../common/PricePrgClient';
import { TightAccordionDetails } from '../../common/StyledComponents';
import { RootState } from '../../store';
import { toPriceString } from '../../utils';
import { showAlertDialog, showConfirmDialog } from '../ui/redux/uiSlices';
import { CostDraggable } from './CostDraggable';
import { createCostGroup, deleteCostGroup, showEditCostGroupDialog } from './redux/calculationSlices';

type OwnProps = {
    costGroup: CostGroupDto;
    index: number;
};

const mapStateToProps = (state: RootState) => ({
    costTypes: state.calculation.costtypes,
    selectedCalculationPricelist: state.calculation.selectedCalculationPricelist,
    currentCalculation: state.calculation.calculation,
    executedCalculationResult: state.calculation.executedCalculationResult,
    productGroups: state.product.productGroups,
    productCategories: state.product.productCategories
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const CostGroupDraggable = ({
    costGroup,
    index,
    selectedCalculationPricelist,
    currentCalculation,
    executedCalculationResult,
    productGroups,
    productCategories
}: Props) => {
    const dispatch = useDispatch();

    const dispatchDeleteCostGroup = (costGroupId: number) => {
        dispatch(
            showConfirmDialog({
                dialogMessage: 'Do you want to delete this cost group?',
                dialogTitle: 'Delete cost group',
                primaryActionName: 'Delete',
                okAction: deleteCostGroup.request({ costGroupId: costGroupId })
            })
        );
    };

    const [isExpanded, setExpanded] = useState(false);

    const handleSetExpanded = (expanded: boolean) => {
        setExpanded(expanded);
    };

    const overrideCostGroup = (costGroupDto: CostGroupDto) => {
        if (currentCalculation.inheritedFrom == null) {
            const request: CreateCostGroupRequest = {
                ...costGroupDto,
                calculationId: currentCalculation.calculationId,
                overridesCostGroupId: costGroupDto.costGroupId
            };
            dispatch(
                createCostGroup.request({
                    pricelistId: selectedCalculationPricelist,
                    costGroupDto: request
                })
            );
        } else {
            dispatch(
                showAlertDialog({
                    dialogTitle: 'Warning!',
                    dialogMessage:
                        'You need to create a calculation on the price list in order to create a cost group on it.',
                    icon: 'warning'
                })
            );
        }
    };

    const editCostGroup = (costGroupDto: CostGroupDto) => {
        dispatch(showEditCostGroupDialog(costGroupDto));
    };

    const inheritedFromButton = (costGroupDto: CostGroupDto) => {
        if (costGroup.inheritedFrom === null || costGroup.inheritedFrom === undefined) {
            return null;
        }

        const overrideCostGroupElement = (
            <Box component="span" sx={{ float: 'right' }}>
                <IconButton onClick={() => overrideCostGroup(costGroupDto)}>
                    <Tooltip
                        title={
                            `Inherited from
                                ${costGroup.inheritedFrom?.inheritedFromPricelistName} (Click to override)` ??
                            ''
                        }
                    >
                        <WarningIcon
                            style={{
                                fill: '#f5c842',
                                float: 'right'
                            }}
                        />
                    </Tooltip>
                </IconButton>
            </Box>
        );

        return (
            <If condition={costGroup?.inheritedFrom != null} then={overrideCostGroupElement} else={null} />
        );
    };

    const renderNonInheritedCostGroup = (costGroupDto: CostGroupDto) => {
        return (
            <span>
                <IconButton
                    sx={{ float: 'right' }}
                    onClick={() => dispatchDeleteCostGroup(costGroup.costGroupId)}
                    component="span"
                >
                    <Tooltip title="Delete">
                        <DeleteIcon />
                    </Tooltip>
                </IconButton>
                <Tooltip title="Edit cost group">
                    <IconButton sx={{ float: 'right' }} onClick={() => editCostGroup(costGroupDto)}>
                        <CreateIcon />
                    </IconButton>
                </Tooltip>
            </span>
        );
    };

    const getCostGroupCalculationResult = (costGroupId: number) => {
        const result =
            executedCalculationResult?.costGroupCalculationResults?.filter(
                c => c.costGroupId === costGroupId
            )[0]?.result ?? 0;
        if (result === 0) {
            return '';
        }

        return toPriceString(result);
    };

    const getCostGroupCalculationAsString = () => {
        return <div>{getCostGroupCalculationResult(costGroup.costGroupId)}</div>;
    };

    const getConditionText = () => {
        return (
            <React.Fragment>
                <Typography color="inherit">Product groups</Typography>
                <ul>
                    {costGroup.appliesToProductGroups?.map(id => (
                        <li key={id}>{productGroups[id].name}</li>
                    )) ?? ''}
                </ul>
                <Typography color="inherit">Product categories 1</Typography>
                <ul>
                    {costGroup.appliesToProductCategories?.map(id => (
                        <li key={id}>{productCategories[id].name}</li>
                    )) ?? ''}
                </ul>
                <Typography color="inherit">Product categories 2</Typography>
                <ul>
                    {costGroup.appliesToProductCategories2?.map(id => (
                        <li key={id}>{productCategories[id].name}</li>
                    )) ?? ''}
                </ul>
            </React.Fragment>
        );
    };

    return (
        <div>
            <Draggable
                key={costGroup.costGroupId}
                draggableId={costGroup.costGroupId?.toString() || ''}
                index={index}
            >
                {provided => (
                    <Box
                        component="div"
                        sx={{ padding: '3px', margin: '8px', backgroundColor: 'white', paddingBottom: '8px' }}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                    >
                        <Accordion
                            expanded={isExpanded}
                            onChange={(_, expanded) => handleSetExpanded(expanded)}
                        >
                            <AccordionSummary
                                sx={{
                                    content: {
                                        margin: '0',
                                        '&.Mui-expanded': {
                                            margin: '0'
                                        }
                                    }
                                }}
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Grid container direction="row" spacing={1}>
                                    <Grid item>
                                        <Box
                                            component="span"
                                            {...provided.dragHandleProps}
                                            sx={{
                                                display: 'inline-block',
                                                color: 'lightgrey',
                                                marginRight: '8px',
                                                marginTop: '10px'
                                            }}
                                        >
                                            <DragHandleIcon />
                                        </Box>
                                    </Grid>
                                    <Grid item xs sx={{ marginTop: '13px' }}>
                                        <Box component="span">
                                            <If
                                                condition={costGroup?.isDisabled ?? false}
                                                then={
                                                    <s>
                                                        <strong style={{ verticalAlign: 'top' }}>
                                                            {costGroup.name}
                                                        </strong>
                                                    </s>
                                                }
                                                else={
                                                    <strong style={{ verticalAlign: 'top' }}>
                                                        {costGroup.name}
                                                    </strong>
                                                }
                                            />
                                        </Box>
                                        <If
                                            condition={
                                                ((costGroup &&
                                                    (costGroup.appliesToProductGroups ||
                                                        costGroup.appliesToProductCategories ||
                                                        costGroup.appliesToProductCategories2) &&
                                                    ((costGroup.appliesToProductGroups?.length ?? 0) > 0 ??
                                                        false)) ||
                                                    (costGroup.appliesToProductCategories?.length ?? 0) > 0 ||
                                                    (costGroup.appliesToProductCategories2?.length ?? 0) >
                                                        0) ??
                                                false
                                            }
                                            then={
                                                <Tooltip
                                                    sx={theme => ({
                                                        backgroundColor: '#f5f5f9',
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                        maxWidth: 220,
                                                        fontSize: theme.typography.pxToRem(12),
                                                        border: '1px solid #dadde9',
                                                        marginTop: '0px'
                                                    })}
                                                    title={getConditionText()}
                                                >
                                                    <IconButton style={{ paddingTop: '0px' }}>
                                                        <ErrorOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            else={null}
                                        />
                                        <If
                                            condition={
                                                getCostGroupCalculationResult(costGroup.costGroupId) !== ''
                                            }
                                            then={getCostGroupCalculationAsString()}
                                            else={null}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <span style={{ float: 'right' }}>
                                            <If
                                                condition={
                                                    costGroup.inheritedFrom == null ||
                                                    costGroup.inheritedFrom === undefined
                                                }
                                                then={renderNonInheritedCostGroup(costGroup)}
                                                else={inheritedFromButton(costGroup)}
                                            />
                                        </span>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <TightAccordionDetails style={{ margin: '0' }}>
                                <If
                                    condition={isExpanded}
                                    then={
                                        <CostDraggable
                                            costs={costGroup.costs}
                                            type={'costGroup_' + costGroup.costGroupId}
                                        />
                                    }
                                    else={null}
                                />
                            </TightAccordionDetails>
                        </Accordion>
                    </Box>
                )}
            </Draggable>
        </div>
    );
};
export default connect(mapStateToProps)(CostGroupDraggable);
